/**
 * เมื่อลูกค้าสั่งก่อนจ่ายทีหลัง — เปิด Payment dialog ที่ Order Detail
 * ระบบจะเซ็ต order นี้ให้ Customer Display แสดงรายการของ order นั้น
 * (แทนที่จะแสดง cart ปัจจุบัน)
 */

import type { CartItemSnapshot } from "../../domain/contracts/offline-sync";
import type { CartItem } from "../context/CartContext";

const STORAGE_KEY = "posCustomerDisplayOrder";
const EVENT_NAME = "customer-display-order-updated";

export type CustomerDisplayOrderSnapshot = {
  items: CartItem[];
  grandTotal: number;
  discountTotal?: number;
};

export type CustomerDisplayOrder = {
  orderId: string;
  branchId: string;
  /** local order / offline — ส่งรายการ inline แทน fetch API */
  snapshot?: CustomerDisplayOrderSnapshot;
};

export function cartSnapshotsToDisplayCartItems(
  items: CartItemSnapshot[],
): CartItem[] {
  return items.map((item) => ({
    id: item.id,
    productId: item.productId,
    productName: item.productName,
    variantId: item.variantId,
    variantName: item.variantName,
    price: item.price,
    quantity: item.quantity,
    modifiers: item.modifiers.map((m) => ({
      modifierId: m.modifierId,
      modifierName: m.modifierName,
      optionValue: m.optionValue,
      optionLabel: m.optionLabel,
      priceDelta: m.priceDelta,
    })),
    selectedOptions: item.selectedOptions,
    note: item.note,
    totalPrice: item.totalPrice,
  }));
}

export function getCustomerDisplayOrder(): CustomerDisplayOrder | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CustomerDisplayOrder;
    if (parsed?.orderId && parsed?.branchId) return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
}

export function setCustomerDisplayOrder(
  orderId: string,
  branchId: string,
  snapshot?: CustomerDisplayOrderSnapshot,
): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ orderId, branchId, snapshot } satisfies CustomerDisplayOrder),
  );
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function clearCustomerDisplayOrder(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function addCustomerDisplayOrderListener(
  callback: () => void,
): () => void {
  const handler = () => callback();
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback();
  };
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener("storage", storageHandler);
  };
}
